import { useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Filter, X } from "lucide-react";
import { guideKategorien, type GuideKategorie, type GuideTag } from "@/data/guides";

export interface GuideFilterState {
  kategorie: GuideKategorie | null;
  tags: GuideTag[];
  sortBy: "datum" | "lesezeit" | "titel";
  order: "asc" | "desc";
}

interface GuideFilterSidebarProps {
  filters: GuideFilterState;
  onChange: (filters: GuideFilterState) => void;
  mobile?: boolean;
}

const allTags: { id: GuideTag; name: string }[] = [
  { id: "einsteiger", name: "Einsteiger" },
  { id: "fortgeschritten", name: "Fortgeschritten" },
  { id: "pflege", name: "Pflege" },
  { id: "winter", name: "Winter" },
  { id: "sommer", name: "Sommer" },
  { id: "trekking", name: "Trekking" },
  { id: "familie", name: "Familie" },
  { id: "festival", name: "Festival" },
  { id: "wissen", name: "Wissen" },
  { id: "praxis", name: "Praxis" },
];

const sortOptions = [
  { value: "datum", label: "Neueste zuerst" },
  { value: "lesezeit", label: "Kürzeste zuerst" },
  { value: "titel", label: "A-Z" },
];

function FilterContent({ filters, onChange, showReset = true }: { filters: GuideFilterState; onChange: (filters: GuideFilterState) => void; showReset?: boolean }) {
  const hasActiveFilters = filters.kategorie !== null || filters.tags.length > 0;

  const resetFilters = () => {
    onChange({
      kategorie: null,
      tags: [],
      sortBy: "datum",
      order: "desc",
    });
  };

  const toggleTag = (tag: GuideTag) => {
    const newTags = filters.tags.includes(tag)
      ? filters.tags.filter(t => t !== tag)
      : [...filters.tags, tag];
    onChange({ ...filters, tags: newTags });
  };

  return (
    <div className="space-y-6">
      {/* Reset - only show if showReset is true */}
      {showReset && hasActiveFilters && (
        <Button variant="ghost" size="sm" onClick={resetFilters} className="w-full">
          <X className="h-4 w-4 mr-2" />
          Filter zurücksetzen
        </Button>
      )}

      {/* Kategorie */}
      <div>
        <h3 className="font-semibold mb-3">Kategorie</h3>
        <div className="space-y-2">
          <button
            onClick={() => onChange({ ...filters, kategorie: null })}
            className={`w-full text-left px-3 py-2 rounded-md transition-colors ${
              filters.kategorie === null
                ? "bg-primary text-primary-foreground"
                : "hover:bg-muted"
            }`}
          >
            Alle Ratgeber
          </button>
          {guideKategorien.map(kat => (
            <button
              key={kat.id}
              onClick={() => onChange({ ...filters, kategorie: kat.id })}
              className={`w-full text-left px-3 py-2 rounded-md transition-colors ${
                filters.kategorie === kat.id
                  ? "bg-primary text-primary-foreground"
                  : "hover:bg-muted"
              }`}
            >
              <div className="font-medium">{kat.name}</div>
              <div className="text-xs opacity-90">{kat.beschreibung}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Tags */}
      <div>
        <h3 className="font-semibold mb-3">Themen</h3>
        <div className="flex flex-wrap gap-2">
          {allTags.map(tag => (
            <Badge
              key={tag.id}
              variant={filters.tags.includes(tag.id) ? "default" : "outline"}
              className="cursor-pointer"
              onClick={() => toggleTag(tag.id)}
            >
              {tag.name}
            </Badge>
          ))}
        </div>
      </div>

      {/* Sortierung */}
      <div>
        <h3 className="font-semibold mb-3">Sortierung</h3>
        <div className="space-y-2">
          {sortOptions.map(option => (
            <button
              key={option.value}
              onClick={() =>
                onChange({
                  ...filters,
                  sortBy: option.value as "datum" | "lesezeit" | "titel",
                  order: option.value === "titel" ? "asc" : "desc",
                })
              }
              className={`w-full text-left px-3 py-2 rounded-md transition-colors ${
                filters.sortBy === option.value
                  ? "bg-primary text-primary-foreground"
                  : "hover:bg-muted"
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

export function GuideFilterSidebar({ filters, onChange, mobile }: GuideFilterSidebarProps) {
  const [open, setOpen] = useState(false);

  if (mobile) {
    return (
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetTrigger asChild>
          <Button variant="outline" size="sm">
            <Filter className="h-4 w-4 mr-2" />
            Filter
          </Button>
        </SheetTrigger>
        <SheetContent side="left" className="w-80 overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Filter</SheetTitle>
          </SheetHeader>
          <div className="mt-6">
            <FilterContent filters={filters} onChange={onChange} showReset={true} />
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  return <FilterContent filters={filters} onChange={onChange} showReset={true} />;
}
